import styled from '@emotion/styled';
import { upperFirst } from 'lodash';
import React, { FC } from 'react';
import { ImpulseSpinner } from '~Components/ImpulseSpinner';
import { LoyaltyMember, useButton } from '~Hooks';
import useI18n from '~Translations';
import { CardIdentity } from '~reducers';
import { handyBreakpoint } from '~Styles/breakpoints';
import { memberSince } from '../helpers';
import { BrandMark, CheckIcon, ContactlessIcon } from '../icons';
import {
  Body,
  C,
  CancelButton,
  CardValue,
  CloseButton,
  ConfirmButton,
  DisconnectButton,
  ErrorNote,
  Footer,
  ReleasingBar,
  SectionLabel,
  SpinnerRow,
  StatLabel,
  StatRow,
  StatValue,
  TRANSITION,
} from '../styles';

/** The signature: staff act on one number, so it is the only thing set large. */
const DiscountBlock = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-top: 22px;
  padding: 20px 22px;
  border-radius: 14px;
  background: ${C.accentWash};
  border: 1px solid ${C.accentEdge};

  ${handyBreakpoint} {
    margin-top: 16px;
    padding: 15px 17px;
  }
`;

const DiscountFigure = styled.div`
  color: ${C.accent};
  font-size: 60px;
  font-weight: 700;
  line-height: 0.85;
  letter-spacing: -0.04em;
  font-variant-numeric: tabular-nums;

  ${handyBreakpoint} {
    font-size: 48px;
  }
`;

const DiscountLabel = styled.div`
  color: ${C.textSecondary};
  font-size: 13px;
  line-height: 1.35;

  ${handyBreakpoint} {
    font-size: 12px;
  }
`;

const StampRow = styled.div`
  padding: 14px 16px;
  margin-bottom: 8px;
  border-radius: 12px;
  background: ${C.surfaceInset};
  border: 1px solid ${C.hairline};

  ${handyBreakpoint} {
    padding: 11px 13px;
  }
`;

const StampHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;

const StampTitle = styled.div`
  color: ${C.textPrimary};
  font-size: 14px;
  font-weight: 600;

  ${handyBreakpoint} {
    font-size: 13px;
  }
`;

const StampCount = styled.div`
  color: ${C.accent};
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  ${handyBreakpoint} {
    font-size: 13px;
  }
`;

const Track = styled.div`
  position: relative;
  height: 6px;
  margin-top: 12px;
  border-radius: 3px;
  background: ${C.hairline};
  overflow: hidden;

  ${handyBreakpoint} {
    height: 5px;
    margin-top: 10px;
  }
`;

const Fill = styled.div<{ pct: number }>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ pct }) => pct}%;
  border-radius: 3px;
  background: ${C.accent};
`;

const ConnectCardRow = styled.button<{ touching?: boolean; checked: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  margin-top: 22px;
  padding: 16px;
  text-align: left;
  cursor: pointer;
  border-radius: 12px;
  background: ${({ touching }) => (touching ? C.surfacePressed : C.surfaceInset)};
  border: 1px solid ${({ checked }) => (checked ? C.accentEdge : C.hairline)};
  transition: background ${TRANSITION}, border-color ${TRANSITION};

  &:focus-visible {
    outline: 2px solid ${C.accent};
    outline-offset: 2px;
  }

  ${handyBreakpoint} {
    gap: 11px;
    margin-top: 16px;
    padding: 13px;
  }
`;

const Checkbox = styled.span<{ checked: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 24px;
  height: 24px;
  border-radius: 7px;
  background: ${({ checked }) => (checked ? C.accent : 'transparent')};
  border: 1.5px solid ${({ checked }) => (checked ? C.accent : C.textMuted)};
  transition: background ${TRANSITION}, border-color ${TRANSITION};

  ${handyBreakpoint} {
    width: 20px;
    height: 20px;
  }
`;

const ConnectCardTitle = styled.span`
  display: block;
  color: ${C.textPrimary};
  font-size: 14px;
  font-weight: 600;

  ${handyBreakpoint} {
    font-size: 13px;
  }
`;

const ConnectCardHint = styled.span`
  display: block;
  margin-top: 3px;
  color: ${C.textSecondary};
  font-size: 12px;
  line-height: 1.35;

  ${handyBreakpoint} {
    font-size: 11px;
  }
`;

interface Props {
  /** Null while an already-attached member's details are on their way in. */
  member: LoyaltyMember | null;
  /** Why `member` could not be loaded, when it is null for that reason. */
  loadError: string | null;
  /** The tapped card shown at the bottom of the panel, when there is one. */
  cardIdentity: CardIdentity | null;
  /** True when the member on screen is already attached to the cart. */
  isConnected: boolean;
  /** An acquisition is being handed back to the terminal right now. */
  isReleasing: boolean;
  /** The member was resolved from their own card, so there is nothing to link. */
  cardBelongsToMember: boolean;
  connectCard: boolean;
  onToggleConnectCard: () => void;
  onConfirm: () => void;
  onCancel: () => void;
  onClose: () => void;
  onDisconnect: () => void;
}

/** The member on screen: their discount, stamp progress, and details. */
const MemberView: FC<Props> = ({
  member,
  loadError,
  cardIdentity,
  isConnected,
  isReleasing,
  cardBelongsToMember,
  connectCard,
  onToggleConnectCard,
  onConfirm,
  onCancel,
  onClose,
  onDisconnect,
}) => {
  const { i18n } = useI18n();

  const connectCardButton = useButton({ onClick: onToggleConnectCard });
  const confirmButton = useButton({ onClick: onConfirm, disabled: isReleasing || !member });
  const cancelButton = useButton({ onClick: onCancel, disabled: isReleasing });
  const closeButton = useButton({ onClick: onClose, disabled: isReleasing });
  const disconnectButton = useButton({ onClick: onDisconnect, disabled: isReleasing });

  const level = member?.currentMembershipLevel ?? null;
  const nextLevel = member?.nextMembershipLevel ?? null;
  /** Only the campaigns still running; a finished card's progress is history. */
  const activeStampCards =
    member?.stampCards.filter((progress) => progress.stampCard.isActive) ?? [];

  return (
    <>
      <Body>
        {/* An attached member reopened, details still on their way in. */}
        {!member && !loadError && (
          <SpinnerRow>
            <ImpulseSpinner size={32} />
          </SpinnerRow>
        )}

        {!member && loadError && <ErrorNote>{loadError}</ErrorNote>}

        {member && (
          <>
            {level && level.discountPercentage > 0 && (
              <DiscountBlock>
                <DiscountFigure>{level.discountPercentage}%</DiscountFigure>
                <DiscountLabel>{i18n('Loyalty.MemberDiscount')}</DiscountLabel>
              </DiscountBlock>
            )}

            {activeStampCards.length > 0 && (
              <>
                <SectionLabel>{i18n('Loyalty.StampCards')}</SectionLabel>
                {activeStampCards.map((progress) => (
                  <StampRow key={progress.stampCard.id}>
                    <StampHeader>
                      <StampTitle>{i18n(progress.stampCard.name)}</StampTitle>
                      <StampCount>
                        {progress.currentStampCount} / {progress.stampCard.stampsRequired}
                      </StampCount>
                    </StampHeader>
                    <Track>
                      <Fill
                        pct={Math.min(
                          100,
                          Math.round(
                            (progress.currentStampCount / progress.stampCard.stampsRequired) * 100,
                          ),
                        )}
                      />
                    </Track>
                  </StampRow>
                ))}
              </>
            )}

            {!cardBelongsToMember && !isConnected && (
              <ConnectCardRow
                type="button"
                checked={connectCard}
                role="checkbox"
                aria-checked={connectCard}
                {...connectCardButton}>
                <Checkbox checked={connectCard}>{connectCard && <CheckIcon />}</Checkbox>
                <span>
                  <ConnectCardTitle>{i18n('Loyalty.ConnectCard')}</ConnectCardTitle>
                  <ConnectCardHint>{i18n('Loyalty.ConnectCardHint')}</ConnectCardHint>
                </span>
              </ConnectCardRow>
            )}

            <SectionLabel>{i18n('Loyalty.Card')}</SectionLabel>
            {member.email && (
              <StatRow>
                <StatLabel>{i18n('Loyalty.Email')}</StatLabel>
                <StatValue>{member.email}</StatValue>
              </StatRow>
            )}
            <StatRow>
              <StatLabel>{i18n('Loyalty.MemberSince')}</StatLabel>
              <StatValue>{memberSince(member.registeredAt)}</StatValue>
            </StatRow>
            <StatRow>
              <StatLabel>{upperFirst(i18n('Loyalty.Points'))}</StatLabel>
              <StatValue>{member.points.toLocaleString('sv-SE')}</StatValue>
            </StatRow>
            {(level || nextLevel) && (
              <StatRow>
                <StatLabel>{i18n('Loyalty.NextLevel')}</StatLabel>
                <StatValue>
                  {nextLevel
                    ? `${i18n(nextLevel.name)}${
                        nextLevel.requiredPointsToAchieve
                          ? ` · ${nextLevel.requiredPointsToAchieve.toLocaleString(
                              'sv-SE',
                            )} ${i18n('Loyalty.Points')}`
                          : ''
                      }`
                    : i18n('Loyalty.TopLevel')}
                </StatValue>
              </StatRow>
            )}
            <StatRow>
              <StatLabel>{i18n('Loyalty.ConnectedCards')}</StatLabel>
              <StatValue>{member.connectedCards.length}</StatValue>
            </StatRow>
            {cardIdentity?.maskedPan && (
              <StatRow>
                <StatLabel>{i18n('Loyalty.Card')}</StatLabel>
                <CardValue>
                  <BrandMark brand={cardIdentity.brand} />
                  {cardIdentity.maskedPan}
                </CardValue>
              </StatRow>
            )}
          </>
        )}
      </Body>

      {isReleasing ? (
        <Footer>
          <ReleasingBar>
            <ContactlessIcon size={22} animated />
            {i18n('Loyalty.Cancelling')}
          </ReleasingBar>
        </Footer>
      ) : isConnected ? (
        <Footer>
          <DisconnectButton type="button" {...disconnectButton}>
            {i18n('Loyalty.Disconnect')}
          </DisconnectButton>
          <CloseButton type="button" {...closeButton}>
            {i18n('Loyalty.Close')}
          </CloseButton>
        </Footer>
      ) : (
        <Footer>
          <CancelButton type="button" {...cancelButton}>
            {i18n('Loyalty.Cancel')}
          </CancelButton>
          <ConfirmButton type="button" {...confirmButton}>
            {i18n('Loyalty.Confirm')}
          </ConfirmButton>
        </Footer>
      )}
    </>
  );
};

export default MemberView;
